/**
 * WizardStepComposableTraits
 *
 * Step 4 of the composable collection wizard.
 * Allows creators to define trait categories and upload trait value images.
 *
 * The base image is integrated into the layer list as a visually distinct row.
 * Its position determines which categories render below vs above it.
 *
 * Trait value management (image upload, name/supply editing) happens in a
 * focused modal rather than inline, keeping the layer stack compact.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../../ui/components/Button';
import { TextField } from '../../../ui/components/TextField';
import { FeatherPlus, FeatherTrash2, FeatherUpload, FeatherArrowUp, FeatherArrowDown, FeatherEdit2, FeatherX } from '@subframe/core';
import {
  getTraitCategories,
  createTraitCategory,
  deleteTraitCategory,
  updateTraitCategory,
  createTraitValues,
  deleteTraitValue,
  updateTraitValue,
  uploadToPresignedUrl,
  presignBaseImage,
  updateBaseImageZIndex,
  type TraitCategory,
} from '../../../api/compose';

export interface WizardStepComposableTraitsProps {
  launchId: string;
  baseImageUrl?: string | null;
  onBaseImageUploaded?: (url: string) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  onError?: (error: string) => void;
}

export const WizardStepComposableTraits: React.FC<WizardStepComposableTraitsProps> = ({
  launchId,
  baseImageUrl,
  onBaseImageUploaded,
  onUploadingChange,
  onError,
}) => {
  const [categories, setCategories] = useState<TraitCategory[]>([]);
  const [baseImageZIndex, setBaseImageZIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [uploadingBaseImage, setUploadingBaseImage] = useState(false);
  const [baseImageWarning, setBaseImageWarning] = useState<string | null>(null);
  const baseImageInputRef = React.useRef<HTMLInputElement>(null);

  // New category form
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  // Upload state (kept in parent for onUploadingChange callback)
  const [addingValuesFor, setAddingValuesFor] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);

  const isAnyUploading = uploadingBaseImage || addingValuesFor !== null;

  useEffect(() => {
    onUploadingChange?.(isAnyUploading);
  }, [isAnyUploading, onUploadingChange]);

  const loadCategories = useCallback(async () => {
    try {
      const result = await getTraitCategories(launchId);
      setCategories(result.categories);
      setBaseImageZIndex(result.base_image_z_index);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [launchId, onError]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm('Delete this category and all its trait values?')) return;

    try {
      if (editingCategoryId === categoryId) setEditingCategoryId(null);
      await deleteTraitCategory(launchId, categoryId);
      await loadCategories();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to delete category');
    }
  };

  const handleDeleteValue = async (categoryId: string, valueId: string) => {
    try {
      await deleteTraitValue(launchId, categoryId, valueId);
      await loadCategories();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to delete value');
    }
  };

  const handleAddValues = async (
    categoryId: string,
    files: File[],
    supplies: number[]
  ) => {
    try {
      setAddingValuesFor(categoryId);
      setUploadProgress({ current: 0, total: files.length });

      const values = files.map((file, i) => ({
        name: file.name.replace(/\.[^.]+$/, ''),
        total_supply: supplies[i],
        content_type: file.type || 'image/png',
      }));

      const created = await createTraitValues(launchId, categoryId, values);

      for (let i = 0; i < created.length; i++) {
        await uploadToPresignedUrl(created[i].uploadUrl, files[i]);
        setUploadProgress({ current: i + 1, total: created.length });
      }

      await loadCategories();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to add trait values');
    } finally {
      setAddingValuesFor(null);
      setUploadProgress(null);
    }
  };

  const handleUpdateSupply = async (
    categoryId: string,
    valueId: string,
    newSupply: number
  ) => {
    try {
      await updateTraitValue(launchId, categoryId, valueId, { total_supply: newSupply });
      await loadCategories();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to update supply');
    }
  };

  const handleUpdateName = async (
    categoryId: string,
    valueId: string,
    newName: string
  ) => {
    try {
      await updateTraitValue(launchId, categoryId, valueId, { name: newName });
      await loadCategories();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to update name');
    }
  };

  // Move a category toward the front (higher z_index) or back (lower z_index).
  const handleMoveLayer = async (categoryId: string, direction: 'raise' | 'lower') => {
    const sorted = [...categories].sort((a, b) => a.z_index - b.z_index);
    const idx = sorted.findIndex((c) => c.id === categoryId);
    if (idx === -1) return;

    const swapIdx = direction === 'raise' ? idx + 1 : idx - 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const catA = sorted[idx];
    const catB = sorted[swapIdx];
    const tempIndex = 999999;

    try {
      await updateTraitCategory(launchId, catA.id, { z_index: tempIndex });
      await updateTraitCategory(launchId, catB.id, { z_index: catA.z_index });
      await updateTraitCategory(launchId, catA.id, { z_index: catB.z_index });
      await loadCategories();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to reorder layers');
    }
  };

  const handleMoveBase = async (direction: 'up' | 'down') => {
    const newZIndex = direction === 'up' ? baseImageZIndex + 1 : baseImageZIndex - 1;
    if (newZIndex < 0 || newZIndex > categories.length) return;

    try {
      setBaseImageZIndex(newZIndex);
      await updateBaseImageZIndex(launchId, newZIndex);
    } catch (err) {
      setBaseImageZIndex(baseImageZIndex);
      onError?.(err instanceof Error ? err.message : 'Failed to move base image');
    }
  };

  const checkTransparency = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = Math.min(img.width, 256);
        canvas.height = Math.min(img.height, 256);
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(false); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        let transparentPixels = 0;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 250) transparentPixels++;
        }
        resolve(transparentPixels / (data.length / 4) > 0.01);
      };
      img.onerror = () => resolve(false);
      img.src = URL.createObjectURL(file);
    });
  };

  const handleBaseImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      setUploadingBaseImage(true);
      setBaseImageWarning(null);

      const hasTransparency = await checkTransparency(file);
      if (!hasTransparency) {
        setBaseImageWarning(
          'This image doesn\'t appear to have a transparent background. If you have a Background trait category below the base, the base image should be a transparent PNG so the background layer shows through.'
        );
      }

      const { uploadUrl, cdnUrl } = await presignBaseImage(launchId, file.type || 'image/png');
      await uploadToPresignedUrl(uploadUrl, file);
      onBaseImageUploaded?.(cdnUrl);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to upload base image');
    } finally {
      setUploadingBaseImage(false);
    }
  };

  const categoriesWithValues = useMemo(
    () => categories.filter((cat) => cat.values.some((v) => v.image_url)),
    [categories]
  );

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.z_index - b.z_index),
    [categories]
  );
  const categoriesBelowBase = useMemo(
    () => sortedCategories.filter((c) => c.z_index < baseImageZIndex),
    [sortedCategories, baseImageZIndex]
  );
  const categoriesAboveBase = useMemo(
    () => sortedCategories.filter((c) => c.z_index >= baseImageZIndex),
    [sortedCategories, baseImageZIndex]
  );
  const maxZIndex = sortedCategories.length > 0 ? sortedCategories[sortedCategories.length - 1].z_index : 0;
  const minZIndex = sortedCategories.length > 0 ? sortedCategories[0].z_index : 0;

  const editingCategory = editingCategoryId
    ? categories.find((c) => c.id === editingCategoryId) ?? null
    : null;

  if (loading) {
    return <div className="text-body font-body text-subtext-color">Loading trait categories...</div>;
  }

  // Compact category row for the layer stack
  const renderCategoryRow = (category: TraitCategory) => (
    <div
      key={category.id}
      className="rounded-lg overflow-hidden border border-neutral-border hover:border-brand-300 transition-colors cursor-pointer"
      onClick={() => setEditingCategoryId(category.id)}
    >
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Show first trait image as thumbnail, or placeholder */}
          {category.values.length > 0 && category.values[0].image_url ? (
            <img
              src={category.values[0].image_url}
              alt=""
              className="w-8 h-8 rounded object-cover border border-neutral-border flex-shrink-0"
            />
          ) : (
            <div className="w-8 h-8 rounded border border-dashed border-neutral-border flex items-center justify-center flex-shrink-0">
              <FeatherUpload className="w-3 h-3 text-subtext-color" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-body-bold font-body-bold text-default-font truncate">
              {category.name}
            </div>
            <div className="text-caption font-caption text-subtext-color">
              {category.values.length} value{category.values.length !== 1 ? 's' : ''} &middot;{' '}
              {category.values.reduce((s, v) => s + v.total_supply, 0).toLocaleString()} supply
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => handleMoveLayer(category.id, 'raise')}
            disabled={category.z_index >= maxZIndex || isAnyUploading}
            className="p-1 text-subtext-color hover:text-default-font disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move to front"
          >
            <FeatherArrowUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleMoveLayer(category.id, 'lower')}
            disabled={category.z_index <= minZIndex || isAnyUploading}
            className="p-1 text-subtext-color hover:text-default-font disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move to back"
          >
            <FeatherArrowDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleDeleteCategory(category.id)}
            disabled={isAnyUploading}
            className="p-1 text-subtext-color hover:text-error-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <FeatherTrash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  // Base image row
  const renderBaseImageRow = () => (
    <div className="rounded-lg overflow-hidden border-2 border-brand-400">
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          {baseImageUrl ? (
            <img
              src={baseImageUrl}
              alt="Base image"
              className="w-8 h-8 rounded object-cover border border-brand-300 flex-shrink-0"
            />
          ) : (
            <div className="w-8 h-8 rounded border-2 border-dashed border-brand-300 flex items-center justify-center flex-shrink-0">
              <FeatherUpload className="w-3 h-3 text-brand-400" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="text-body-bold font-body-bold text-default-font">Base Character</span>
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-200 text-brand-700">
                BASE
              </span>
            </div>
            <p className="text-caption font-caption text-subtext-color">
              Present on every NFT
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => handleMoveBase('up')}
            disabled={baseImageZIndex >= categories.length || isAnyUploading}
            className="p-1 text-subtext-color hover:text-default-font disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move to front"
          >
            <FeatherArrowUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleMoveBase('down')}
            disabled={baseImageZIndex <= 0 || isAnyUploading}
            className="p-1 text-subtext-color hover:text-default-font disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move to back"
          >
            <FeatherArrowDown className="w-4 h-4" />
          </button>
          <Button
            variant="neutral-secondary"
            size="small"
            onClick={() => baseImageInputRef.current?.click()}
            disabled={isAnyUploading}
            loading={uploadingBaseImage}
          >
            {baseImageUrl ? 'Replace' : 'Upload'}
          </Button>
        </div>
      </div>
      {baseImageWarning && (
        <div className="px-3 pb-3">
          <p className="text-caption font-caption text-warning-600">
            {baseImageWarning}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* How It Works */}
      <div>
        <h3 className="text-body-bold font-body-bold text-default-font mb-2">How Build Your Own Works</h3>
        <p className="text-caption font-caption text-subtext-color mb-3">
          Instead of uploading finished NFTs, you define <strong className="text-default-font">trait categories</strong> (e.g. Background, Body, Eyes, Hat) and upload individual images for each trait value. When collectors mint, they receive a single random trait NFT. They can then <strong className="text-default-font">burn multiple trait NFTs to build a new combined NFT</strong> with those traits layered into one image.
        </p>
        <div className="text-caption font-caption text-subtext-color space-y-1">
          <p><strong className="text-default-font">1. Upload a base image</strong> — This is your character or subject that appears on every NFT. Use a transparent PNG if you have a separate Background category.</p>
          <p><strong className="text-default-font">2. Create categories and set layer order</strong> — Each category is a layer. The list shows front-to-back order. Use the arrows to arrange them. Categories above the base render in front of it (e.g. hats, accessories), categories below render behind it (e.g. backgrounds).</p>
          <p><strong className="text-default-font">3. Upload trait images</strong> — Click a category to open it and upload transparent PNGs for each trait value. Set the supply for each.</p>
          <p><strong className="text-default-font">4. Collectors mint and build</strong> — Each mint gives a random single-trait NFT. Holders pick traits, burn them, and build a combined NFT.</p>
        </div>
      </div>

      <hr className="border-neutral-border" />

      {/* Hidden file input for base image upload */}
      <input
        ref={baseImageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleBaseImageUpload}
      />

      {/* New Category Modal */}
      {showNewCategoryModal && (
        <NewCategoryModal
          adding={addingCategory}
          onAdd={async (name) => {
            setNewCategoryName(name);
            try {
              setAddingCategory(true);
              const nextZIndex = categories.length;
              const created = await createTraitCategory(launchId, {
                name: name.trim(),
                z_index: nextZIndex,
              });
              const result = await getTraitCategories(launchId);
              setCategories(result.categories);
              setBaseImageZIndex(result.base_image_z_index);
              setShowNewCategoryModal(false);
              setNewCategoryName('');
              // Flow directly into the image upload modal
              setEditingCategoryId(created.id);
            } catch (err) {
              onError?.(err instanceof Error ? err.message : 'Failed to create category');
            } finally {
              setAddingCategory(false);
            }
          }}
          onClose={() => { setShowNewCategoryModal(false); setNewCategoryName(''); }}
        />
      )}

      {/* Layer Stack — front-to-back (highest z_index at top) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-body-bold font-body-bold text-default-font">Layer Stack</h3>
          <div className="flex items-center gap-3">
            <Button
              variant="brand-secondary"
              size="small"
              onClick={() => setShowNewCategoryModal(true)}
              disabled={isAnyUploading}
              icon={<FeatherPlus />}
            >
              Add Trait Category
            </Button>
          </div>
        </div>

        <p className="text-caption font-caption text-subtext-color">
          The base character appears on every NFT. Categories above it render in front (e.g. hats, accessories). Categories below render behind it (e.g. backgrounds).
        </p>

        {[...categoriesAboveBase].reverse().map((category) => renderCategoryRow(category))}

        {renderBaseImageRow()}

        {[...categoriesBelowBase].reverse().map((category) => renderCategoryRow(category))}

      </div>

      {/* Empty state hint */}
      {categories.length === 0 && (
        <div className="text-center py-6 text-body font-body text-subtext-color">
          <p>No trait categories yet. Click <strong className="text-default-font">Add Trait Category</strong> to get started.</p>
          <p className="text-caption font-caption mt-1">
            Common categories: Background, Body, Eyes, Mouth, Hat, Accessory
          </p>
        </div>
      )}

      {/* Live Composition Preview */}
      {baseImageUrl && categoriesWithValues.length > 0 && (
        <CompositionPreview
          baseImageUrl={baseImageUrl}
          categories={categoriesWithValues}
          baseImageZIndex={baseImageZIndex}
        />
      )}

      {/* Trait Category Modal */}
      {editingCategory && (
        <TraitCategoryModal
          category={editingCategory}
          launchId={launchId}
          isUploading={addingValuesFor === editingCategory.id}
          uploadProgress={addingValuesFor === editingCategory.id ? uploadProgress : null}
          onUpload={(files, supplies) => handleAddValues(editingCategory.id, files, supplies)}
          onDeleteValue={(valueId) => handleDeleteValue(editingCategory.id, valueId)}
          onUpdateName={(valueId, name) => handleUpdateName(editingCategory.id, valueId, name)}
          onUpdateSupply={(valueId, supply) => handleUpdateSupply(editingCategory.id, valueId, supply)}
          onClose={() => setEditingCategoryId(null)}
        />
      )}
    </div>
  );
};

// ==================== Sub-component: Trait Category Modal ====================

interface TraitCategoryModalProps {
  category: TraitCategory;
  launchId: string;
  isUploading: boolean;
  uploadProgress: { current: number; total: number } | null;
  onUpload: (files: File[], supplies: number[]) => void;
  onDeleteValue: (valueId: string) => void;
  onUpdateName: (valueId: string, name: string) => void;
  onUpdateSupply: (valueId: string, supply: number) => void;
  onClose: () => void;
}

const TraitCategoryModal: React.FC<TraitCategoryModalProps> = ({
  category,
  isUploading,
  uploadProgress,
  onUpload,
  onDeleteValue,
  onUpdateName,
  onUpdateSupply,
  onClose,
}) => {
  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isUploading) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isUploading]);

  const [hasPendingFiles, setHasPendingFiles] = useState(false);
  const totalSupply = category.values.reduce((s, v) => s + v.total_supply, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={isUploading ? undefined : onClose} />

      {/* Dialog */}
      <div className="relative z-10 w-full max-w-2xl rounded-lg border border-solid border-neutral-border bg-default-background shadow-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-solid border-neutral-border px-6 py-4 flex-shrink-0">
          <div>
            <h2 className="text-heading-3 font-heading-3 text-default-font">
              {category.name}
            </h2>
            <p className="text-caption font-caption text-subtext-color mt-0.5">
              {category.values.length} value{category.values.length !== 1 ? 's' : ''} &middot; {totalSupply.toLocaleString()} total supply
            </p>
          </div>
          <button
            onClick={isUploading ? undefined : onClose}
            disabled={isUploading}
            className="rounded p-1 hover:bg-neutral-border/50 transition-colors disabled:opacity-30"
          >
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 space-y-4">
          {/* Existing trait values */}
          {category.values.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {category.values.map((value) => (
                <div
                  key={value.id}
                  className="relative group border border-neutral-border rounded-lg overflow-hidden"
                >
                  {value.image_url && (
                    <img
                      src={value.image_url}
                      alt={value.name}
                      className="w-full aspect-square object-cover"
                    />
                  )}
                  <div className="p-2">
                    <InlineNameEditor
                      value={value.name}
                      onChange={(newName) => onUpdateName(value.id, newName)}
                    />
                    <InlineSupplyEditor
                      value={value.total_supply}
                      onChange={(newSupply) => onUpdateSupply(value.id, newSupply)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onDeleteValue(value.id)}
                    className="absolute top-1 right-1 p-1 bg-neutral-900/60 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <FeatherTrash2 className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
            </div>
          ) : !hasPendingFiles ? (
            <div className="text-center py-8 border-2 border-dashed border-neutral-200 rounded-lg">
              <FeatherUpload className="w-8 h-8 text-subtext-color mx-auto mb-3" />
              <p className="text-body font-body text-subtext-color">
                No trait values yet
              </p>
              <p className="text-caption font-caption text-subtext-color mt-1">
                Select images below to add traits to this category.
              </p>
            </div>
          ) : null}

          {/* Uploader */}
          <TraitValueUploader
            categoryId={category.id}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            onUpload={onUpload}
            onPendingChange={setHasPendingFiles}
          />
        </div>
      </div>
    </div>
  );
};

// ==================== Sub-component: New Category Modal ====================

interface NewCategoryModalProps {
  adding: boolean;
  onAdd: (name: string) => void;
  onClose: () => void;
}

const NewCategoryModal: React.FC<NewCategoryModalProps> = ({ adding, onAdd, onClose }) => {
  const [name, setName] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus the input on mount
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !adding) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, adding]);

  const handleSubmit = () => {
    if (name.trim() && !adding) onAdd(name.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={adding ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-md rounded-lg border border-solid border-neutral-border bg-default-background shadow-xl">
        <div className="flex items-center justify-between border-b border-solid border-neutral-border px-6 py-4">
          <h2 className="text-heading-3 font-heading-3 text-default-font">New Trait Category</h2>
          <button
            onClick={adding ? undefined : onClose}
            disabled={adding}
            className="rounded p-1 hover:bg-neutral-border/50 transition-colors disabled:opacity-30"
          >
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-body font-body text-default-font block mb-1.5">Category Name</label>
            <TextField>
              <TextField.Input
                ref={inputRef}
                placeholder="e.g., Eyes, Hat, Background"
                value={name}
                disabled={adding}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
              />
            </TextField>
            <p className="text-caption font-caption text-subtext-color mt-1.5">
              The category will be added to the front of the layer stack.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="neutral-secondary"
              size="medium"
              onClick={onClose}
              disabled={adding}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              size="medium"
              onClick={handleSubmit}
              disabled={!name.trim() || adding}
              loading={adding}
            >
              Create & Add Images
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== Sub-component: Composition Preview ====================

const PREVIEW_COUNT = 10;

interface CompositionPreviewProps {
  baseImageUrl: string;
  categories: TraitCategory[];
  baseImageZIndex: number;
}

function randomPicks(categories: TraitCategory[]): Record<string, number> {
  const picks: Record<string, number> = {};
  for (const cat of categories) {
    if (cat.values.length > 0) {
      picks[cat.id] = Math.floor(Math.random() * cat.values.length);
    }
  }
  return picks;
}

async function drawComposition(
  canvas: HTMLCanvasElement,
  baseImageUrl: string,
  categories: TraitCategory[],
  picks: Record<string, number>,
  baseImageZIndex: number,
): Promise<void> {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const SIZE = 512;
  canvas.width = SIZE;
  canvas.height = SIZE;
  ctx.clearRect(0, 0, SIZE, SIZE);

  const sortedCats = [...categories].sort((a, b) => a.z_index - b.z_index);
  const belowBase = sortedCats.filter((c) => c.z_index < baseImageZIndex);
  const aboveBase = sortedCats.filter((c) => c.z_index >= baseImageZIndex);

  for (const cat of belowBase) {
    const idx = picks[cat.id];
    const value = idx !== undefined ? cat.values[idx] : undefined;
    if (value?.image_url) {
      try {
        const img = await loadPreviewImage(value.image_url);
        ctx.drawImage(img, 0, 0, SIZE, SIZE);
      } catch { /* skip */ }
    }
  }

  try {
    const baseImg = await loadPreviewImage(baseImageUrl);
    ctx.drawImage(baseImg, 0, 0, SIZE, SIZE);
  } catch { /* skip */ }

  for (const cat of aboveBase) {
    const idx = picks[cat.id];
    const value = idx !== undefined ? cat.values[idx] : undefined;
    if (value?.image_url) {
      try {
        const img = await loadPreviewImage(value.image_url);
        ctx.drawImage(img, 0, 0, SIZE, SIZE);
      } catch { /* skip */ }
    }
  }
}

const CompositionPreview: React.FC<CompositionPreviewProps> = ({
  baseImageUrl,
  categories,
  baseImageZIndex,
}) => {
  const canvasRefs = React.useRef<(HTMLCanvasElement | null)[]>([]);
  const [allPicks, setAllPicks] = React.useState<Record<string, number>[]>([]);
  const [composing, setComposing] = React.useState(false);

  const shuffle = React.useCallback(() => {
    setAllPicks(Array.from({ length: PREVIEW_COUNT }, () => randomPicks(categories)));
  }, [categories]);

  // Auto-shuffle whenever categories change (new values uploaded, etc.)
  React.useEffect(() => {
    if (categories.length > 0) {
      shuffle();
    }
  }, [categories, shuffle]);

  React.useEffect(() => {
    if (allPicks.length === 0) return;
    let cancelled = false;
    setComposing(true);

    const drawAll = async () => {
      await Promise.all(
        allPicks.map(async (picks, i) => {
          const canvas = canvasRefs.current[i];
          if (canvas && !cancelled) {
            await drawComposition(canvas, baseImageUrl, categories, picks, baseImageZIndex);
          }
        }),
      );
      if (!cancelled) setComposing(false);
    };

    drawAll();
    return () => { cancelled = true; };
  }, [allPicks, baseImageUrl, categories, baseImageZIndex]);

  const getTraitLabel = (picks: Record<string, number>) =>
    [...categories]
      .sort((a, b) => a.z_index - b.z_index)
      .map((cat) => {
        const idx = picks[cat.id];
        const v = idx !== undefined ? cat.values[idx] : undefined;
        return v ? `${cat.name}: ${v.name}` : null;
      })
      .filter(Boolean);

  return (
    <div className="p-4 rounded-lg border border-neutral-border">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-body-bold font-body-bold text-default-font">Preview</h3>
          <p className="text-caption font-caption text-subtext-color">
            Random combinations of your base image + one trait from each category.
            {composing && <span className="ml-2 text-blue-500">Generating...</span>}
          </p>
        </div>
        <Button
          variant="neutral-secondary"
          size="small"
          onClick={shuffle}
        >
          Shuffle
        </Button>
      </div>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {allPicks.map((picks, i) => (
          <div key={i}>
            <canvas
              ref={(el) => { canvasRefs.current[i] = el; }}
              width={512}
              height={512}
              className="w-full aspect-square rounded-lg bg-neutral-100"
              style={{ imageRendering: 'auto' }}
            />
            <div className="mt-1 space-y-0.5">
              {getTraitLabel(picks).map((label, j) => (
                <div key={j} className="text-[10px] text-subtext-color truncate" title={label!}>
                  {label}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function loadPreviewImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load: ${src}`));
    img.src = src;
  });
}

// ==================== Sub-component: Inline Name Editor ====================

interface InlineNameEditorProps {
  value: string;
  onChange: (newValue: string) => void;
}

const InlineNameEditor: React.FC<InlineNameEditorProps> = ({ value, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onChange(trimmed);
    } else {
      setDraft(value);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        type="text"
        className="w-full text-caption font-caption border border-brand-300 rounded px-1 py-0.5 bg-default-background text-default-font outline-none focus:ring-1 focus:ring-brand-400"
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => { setDraft(value); setEditing(true); }}
      className="flex items-center gap-1 w-full text-left"
      title="Click to rename"
    >
      <span className="text-caption font-caption text-default-font truncate">{value}</span>
      <FeatherEdit2 className="h-2.5 w-2.5 text-subtext-color opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity" />
    </button>
  );
};

// ==================== Sub-component: Inline Supply Editor ====================

interface InlineSupplyEditorProps {
  value: number;
  onChange: (newValue: number) => void;
}

const InlineSupplyEditor: React.FC<InlineSupplyEditorProps> = ({ value, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));

  const commit = () => {
    const parsed = parseInt(draft, 10);
    if (parsed > 0 && parsed !== value) {
      onChange(parsed);
    } else {
      setDraft(String(value));
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        type="number"
        min="1"
        max="100000"
        className="w-full text-caption font-caption border border-brand-300 rounded px-1 py-0.5 bg-default-background text-default-font outline-none focus:ring-1 focus:ring-brand-400"
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(String(value)); setEditing(false); }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => { setDraft(String(value)); setEditing(true); }}
      className="flex items-center gap-1"
      title="Click to edit supply"
    >
      <span className="text-caption font-caption text-subtext-color">Supply: {value.toLocaleString()}</span>
      <FeatherEdit2 className="h-2.5 w-2.5 text-subtext-color opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity" />
    </button>
  );
};

// ==================== Sub-component: Trait Value Uploader ====================

interface PendingFile {
  file: File;
  name: string;
  supply: number;
}

interface TraitValueUploaderProps {
  categoryId: string;
  isUploading: boolean;
  uploadProgress: { current: number; total: number } | null;
  onUpload: (files: File[], supplies: number[]) => void;
  onPendingChange?: (hasPending: boolean) => void;
}

const TraitValueUploader: React.FC<TraitValueUploaderProps> = ({
  categoryId,
  isUploading,
  uploadProgress,
  onUpload,
  onPendingChange,
}) => {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [defaultSupply, setDefaultSupply] = useState(10);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    onPendingChange?.(pendingFiles.length > 0);
  }, [pendingFiles.length, onPendingChange]);

  const prevUploading = React.useRef(false);
  React.useEffect(() => {
    if (prevUploading.current && !isUploading) {
      setPendingFiles([]);
    }
    prevUploading.current = isUploading;
  }, [isUploading]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles: PendingFile[] = Array.from(e.target.files).map((file) => ({
        file,
        name: file.name.replace(/\.[^.]+$/, ''),
        supply: defaultSupply,
      }));
      setPendingFiles((prev) => [...prev, ...newFiles]);
      e.target.value = '';
    }
  };

  const updatePendingSupply = (index: number, supply: number) => {
    setPendingFiles((prev) =>
      prev.map((pf, i) => (i === index ? { ...pf, supply } : pf))
    );
  };

  const removePending = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const setAllSupply = (supply: number) => {
    setPendingFiles((prev) => prev.map((pf) => ({ ...pf, supply })));
  };

  const handleUpload = () => {
    if (pendingFiles.length === 0) return;
    onUpload(
      pendingFiles.map((pf) => pf.file),
      pendingFiles.map((pf) => pf.supply)
    );
  };

  return (
    <div className="pt-3 border-t border-neutral-200 space-y-3">
      {/* Upload progress */}
      {isUploading && uploadProgress && (
        <div className="p-3 bg-brand-50 border border-brand-200 rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-caption font-caption font-bold text-brand-700">
              Uploading traits...
            </span>
            <span className="text-caption font-caption text-brand-600">
              {uploadProgress.current} / {uploadProgress.total}
            </span>
          </div>
          <div className="w-full bg-brand-100 rounded-full h-2 overflow-hidden">
            <div
              className="bg-brand-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%` }}
            />
          </div>
          {uploadProgress.current < uploadProgress.total && (
            <p className="text-[11px] text-brand-500">
              Uploading image {uploadProgress.current + 1} of {uploadProgress.total}...
            </p>
          )}
          {uploadProgress.current === uploadProgress.total && (
            <p className="text-[11px] text-brand-500">
              Finalizing...
            </p>
          )}
        </div>
      )}

      {/* Pending files list */}
      {pendingFiles.length > 0 && !isUploading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-caption font-caption text-subtext-color">
              {pendingFiles.length} file{pendingFiles.length !== 1 ? 's' : ''} ready to upload
            </span>
            <div className="flex items-center gap-2">
              <span className="text-caption font-caption text-subtext-color">Set all supply:</span>
              <input
                type="number"
                min="1"
                max="100000"
                className="w-20 text-caption font-caption border border-neutral-border rounded px-2 py-1 bg-default-background text-default-font"
                value={String(defaultSupply)}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10) || 1;
                  setDefaultSupply(val);
                  setAllSupply(val);
                }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 px-2 pb-1">
            <span className="flex-1 text-caption font-caption text-subtext-color">Name</span>
            <span className="w-20 text-caption font-caption text-subtext-color text-center">Supply</span>
            <span className="w-5" />
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {pendingFiles.map((pf, i) => (
              <div
                key={`${pf.name}-${i}`}
                className="flex items-center gap-2 px-2 py-1.5 rounded bg-neutral-50"
              >
                <span className="flex-1 text-caption font-caption text-default-font truncate">
                  {pf.name}
                </span>
                <input
                  type="number"
                  min="1"
                  max="100000"
                  className="w-20 text-caption font-caption border border-neutral-border rounded px-2 py-1 bg-default-background text-default-font"
                  value={String(pf.supply)}
                  onChange={(e) =>
                    updatePendingSupply(i, parseInt(e.target.value, 10) || 1)
                  }
                />
                <button
                  type="button"
                  onClick={() => removePending(i)}
                  className="p-0.5 text-subtext-color hover:text-error-600"
                >
                  <FeatherTrash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!isUploading && (
        <div className="flex items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button
            variant="neutral-secondary"
            size="medium"
            onClick={() => fileInputRef.current?.click()}
            icon={<FeatherPlus />}
          >
            Select Images
          </Button>
          {pendingFiles.length > 0 && (
            <Button
              variant="brand-primary"
              size="medium"
              onClick={handleUpload}
              icon={<FeatherUpload />}
            >
              Upload {pendingFiles.length} Trait{pendingFiles.length !== 1 ? 's' : ''}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
